/* ============================================================================
 * Organic-Audit binary storage helpers.
 *
 * Audit records (header + report history + other-doc metadata) live in
 * Supabase (organic_audits). The heavy binaries — generated report PDFs
 * and uploaded additional documents — now live in the PRIVATE Supabase
 * Storage bucket `audit-docs`, so they are shared across devices and
 * users (needed for the auditor/consultant portal).
 *
 * Storage layout in the bucket:
 *   reports/{entryId}.pdf   generated report snapshots
 *   docs/{docId}            uploaded additional documents
 *
 * Backwards compatibility: reports/docs generated BEFORE this migration
 * still live only in IndexedDB on the machine that made them. Every
 * loader therefore tries Storage first and falls back to IndexedDB, so
 * legacy snapshots keep opening on Arturo's browser while all NEW blobs
 * go to the cloud.
 * ========================================================================= */

import { saveDocMap, loadDocMap } from "../gfiDB";
import { uploadFileToStorage, createSignedUrlFor, removeFromStorage } from "../../storageImages";

const REPORT_KEY = "audit-report-blobs"; /* legacy IndexedDB map */
const DOC_KEY    = "audit-doc-blobs";    /* legacy IndexedDB map */

const BUCKET = "audit-docs";

/* Build the storage path for a report / doc id. Paths are derived
   purely from the id (no extension) so that save and load always
   resolve to the same key regardless of whether the caller passes a
   filename. The real content-type is preserved in Storage metadata at
   upload time and in the doc's `mime` field in the audit record. */
function reportPath(entryId) { return "reports/" + String(entryId) + ".pdf"; }
function docPath(docId) { return "docs/" + String(docId); }

/* Fetch a Storage object (private bucket) as a Blob via a short-lived
   signed URL. Returns null on any failure so callers can fall back. */
async function fetchStorageBlob(path) {
  try {
    var signed = await createSignedUrlFor(BUCKET, path, 300);
    if (!signed) return null;
    var resp = await fetch(signed);
    if (!resp || !resp.ok) return null;
    return await resp.blob();
  } catch (e) {
    return null;
  }
}

/* ----- Report snapshots ----- */
export async function saveReportBlob(entryId, blob) {
  if (!entryId || !blob) return false;
  try {
    await uploadFileToStorage(BUCKET, reportPath(entryId), blob);
    return true;
  } catch (e) {
    /* Storage failed — fall back to IndexedDB so the snapshot isn't lost. */
    try {
      const map = await loadDocMap(REPORT_KEY);
      map[String(entryId)] = blob;
      await saveDocMap(REPORT_KEY, map);
      return true;
    } catch (e2) { return false; }
  }
}
export async function loadReportBlob(entryId) {
  if (!entryId) return null;
  /* Storage first. */
  var fromStorage = await fetchStorageBlob(reportPath(entryId));
  if (fromStorage) return fromStorage;
  /* Legacy IndexedDB fallback. */
  try {
    const map = await loadDocMap(REPORT_KEY);
    return map[String(entryId)] || null;
  } catch (e) { return null; }
}
export async function deleteReportBlob(entryId) {
  if (!entryId) return false;
  await removeFromStorage(BUCKET, reportPath(entryId));
  try {
    const map = await loadDocMap(REPORT_KEY);
    if (map[String(entryId)]) {
      delete map[String(entryId)];
      await saveDocMap(REPORT_KEY, map);
    }
  } catch (e) {}
  return true;
}
export async function loadReportBlobs(entryIds) {
  /* Returns { entryId: Blob|null }. Loads the legacy IDB map once,
     then resolves each id from Storage (falling back to the map). */
  var map = {};
  try { map = await loadDocMap(REPORT_KEY); } catch (e) { map = {}; }
  const out = {};
  for (var i = 0; i < (entryIds || []).length; i++) {
    var id = String(entryIds[i]);
    var b = await fetchStorageBlob(reportPath(id));
    out[id] = b || map[id] || null;
  }
  return out;
}

/* ----- Uploaded document blobs ----- */
export async function saveDocBlob(docId, blob) {
  if (!docId || !blob) return false;
  try {
    await uploadFileToStorage(BUCKET, docPath(docId), blob);
    return true;
  } catch (e) {
    try {
      const map = await loadDocMap(DOC_KEY);
      map[String(docId)] = blob;
      await saveDocMap(DOC_KEY, map);
      return true;
    } catch (e2) { return false; }
  }
}
export async function loadDocBlob(docId) {
  if (!docId) return null;
  var fromStorage = await fetchStorageBlob(docPath(docId));
  if (fromStorage) return fromStorage;
  try {
    const map = await loadDocMap(DOC_KEY);
    return map[String(docId)] || null;
  } catch (e) { return null; }
}
export async function deleteDocBlob(docId) {
  if (!docId) return false;
  await removeFromStorage(BUCKET, docPath(docId));
  try {
    const map = await loadDocMap(DOC_KEY);
    if (map[String(docId)]) {
      delete map[String(docId)];
      await saveDocMap(DOC_KEY, map);
    }
  } catch (e) {}
  return true;
}
export async function loadDocBlobs(docIds) {
  var map = {};
  try { map = await loadDocMap(DOC_KEY); } catch (e) { map = {}; }
  const out = {};
  for (var i = 0; i < (docIds || []).length; i++) {
    var id = String(docIds[i]);
    var b = await fetchStorageBlob(docPath(id));
    out[id] = b || map[id] || null;
  }
  return out;
}

/* Friendly viewer: open a blob in a new tab. The temporary object URL
   is revoked after a short delay so the user has time to land on the
   page before the URL becomes invalid. */
export function openBlobInTab(blob, filename) {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    if (filename) a.download = filename;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    setTimeout(function () { document.body.removeChild(a); }, 0);
  }
  setTimeout(function () { try { URL.revokeObjectURL(url); } catch (e) {} }, 60000);
}
